import { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { loginState } from '../../utils/authRedirect';

type AccountSection = 'pedidos' | 'perfil';

interface AccountShellProps {
  title: string;
  description: string;
  customerName: string | null;
  unauthorized: boolean;
  onSignOut: () => void;
  children: ReactNode;
}

const NAV_ITEMS: { section: AccountSection; to: string; label: string; iconPath: string }[] = [
  {
    section: 'pedidos',
    to: '/pedidos',
    label: 'Meus pedidos',
    iconPath:
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  },
  {
    section: 'perfil',
    to: '/perfil',
    label: 'Meus dados',
    iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  },
];

const SIGN_OUT_ICON_PATH = 'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9';

const NAV_ITEM_BASE =
  'flex items-center justify-center lg:justify-start gap-2.5 whitespace-nowrap rounded-full lg:rounded-xl px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40';

function NavIcon({ path }: { path: string }) {
  return (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={path} />
    </svg>
  );
}

function firstName(name: string | null) {
  return name?.trim().split(/\s+/)[0] ?? '';
}

export function AccountShell({ title, description, customerName, unauthorized, onSignOut, children }: AccountShellProps) {
  if (unauthorized) return <SessionExpired />;

  const greetingName = firstName(customerName);

  return (
    <div className="max-w-5xl mx-auto px-4 pt-3 pb-10 md:pt-4">
      <header className="mb-4 lg:mb-6 flex items-center gap-4 rounded-2xl bg-champagne/70 border border-champagne-dark px-4 py-4 sm:px-6">
        {greetingName && (
          <span
            aria-hidden="true"
            className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full bg-white border border-champagne-dark flex items-center justify-center font-display text-lg font-semibold text-chocolate-dark"
          >
            {greetingName.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <h1 className="font-display text-xl sm:text-2xl font-semibold text-text-dark leading-tight">{title}</h1>
          <p className="text-sm text-text-medium mt-0.5">
            {greetingName ? `Olá, ${greetingName}. ` : ''}
            {description}
          </p>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 lg:items-start">
        <nav aria-label="Minha conta" className="mb-4 lg:mb-0 lg:sticky lg:top-24">
          <ul className="grid grid-cols-2 lg:flex lg:flex-col gap-2 lg:bg-white lg:rounded-2xl lg:border lg:border-champagne-dark lg:p-2">
            {NAV_ITEMS.map((item) => (
              <li key={item.section}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      NAV_ITEM_BASE,
                      isActive
                        ? 'bg-chocolate-dark text-white lg:bg-champagne lg:text-chocolate-dark'
                        : 'bg-white border border-champagne-dark lg:border-transparent text-text-dark hover:bg-champagne-light',
                    ].join(' ')
                  }
                >
                  <NavIcon path={item.iconPath} />
                  {item.label}
                </NavLink>
              </li>
            ))}
            <li className="hidden lg:block lg:mt-1 lg:pt-1 lg:border-t lg:border-champagne">
              <button
                type="button"
                onClick={onSignOut}
                className={`${NAV_ITEM_BASE} w-full bg-white border border-champagne-dark lg:border-transparent text-text-medium hover:bg-champagne-light hover:text-text-dark`}
              >
                <NavIcon path={SIGN_OUT_ICON_PATH} />
                Sair
              </button>
            </li>
          </ul>
        </nav>

        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}

function SessionExpired() {
  const location = useLocation();
  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm p-8 text-center">
        <span className="w-14 h-14 rounded-full bg-champagne flex items-center justify-center mx-auto mb-4 text-chocolate-dark">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </span>
        <h1 className="font-display text-xl font-semibold text-text-dark mb-2">Sua sessão expirou</h1>
        <p className="text-sm text-text-medium mb-6">Entre de novo para ver seus pedidos e dados.</p>
        <div className="flex flex-col gap-3">
          <Link
            to="/login"
            state={loginState(location)}
            className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors"
          >
            Entrar
          </Link>
          <Link
            to="/catalogo"
            className="w-full px-4 py-2.5 text-sm font-medium text-chocolate border border-chocolate hover:bg-champagne-light rounded-full transition-colors"
          >
            Voltar ao catálogo
          </Link>
        </div>
      </div>
    </div>
  );
}
